<template>
  <section class="UserManagement">
    <Card class="UserManagement__card">
      <section class="UserManagement__mainSection">
        <div class="UserManagement__inner">
          <SearchForm
            id="search"
            class="UserManagement__searchForm"
            name="search"
            :placeholder="messages.common.searchPlaceholder"
            :value="searchWord"
            @inputWord="searchWord = $event"
          />
          <ul class="UserManagement__list">
            <li class="UserManagement__listItem">
              <Button
                class="UserManagement__button"
                :label="messages.user.userInvitation.title"
                dynamics="primary"
                tag="button"
                @click="openSideContents('ADD')"
              />
            </li>
          </ul>
        </div>
        <SelectedSettings
          v-if="checkedItemsCount"
          v-model:isTableChecked="isThChecked"
          v-model:isAllSelected="isAllSelected"
          class="UserManagement__selectedSettings"
          :selectedNum="checkedItemsCount"
          :allNum="totalCount"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :labelName="messages.user.userDelete"
          :onDeleteButtonClick="onClickDeleteModalOpen"
          @update:isAllSelected="toggleAllChecked"
        />
        <UserList
          v-model:isThChecked="isThChecked"
          :displayUsers="displayUsers"
          :isListLoading="isListLoading"
          :headList="headList"
          :isViewActiveUser="true"
          :mailResendNeeded="true"
          :noDataText="noDataText"
          :noDataImgUrl="noDataImageUrl"
          @onCheckHeader="onCheckHeader"
          @onCheckItem="onCheckItem"
          @openSideContents="openSideContents"
          @onClickReSendInvitation="handleInvitationReSend"
        />
        <PaginationBar
          class="UserManagement__pagination"
          :currentPage="currentPage"
          :maxDisplayPages="maxDisplayPages"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :total="totalCount"
          @onClick:pageNumber="movePage"
        />
        <SideContents
          :heading="sideContentHeading"
          :isOpen="isSideContentsOpen"
          :buttonList="getButtonList"
          @update:close="closeSideContents"
        >
          <UserInvitation
            ref="userInvitationRef"
            :mode="userInvitationMode"
            :userData="userData"
            :isSkeleton="isSkeletonForAPI"
            :invitationBasicDetails="invitationBasicDetails"
            :isEditCancel="isEditCancel"
            :activeUsers="activeUsers"
            @update:inviteSuccess="showFeedback"
            @update:updateSuccess="showUpdateFeedback"
            @update:passwordResetSuccess="showPasswordResetModal"
          ></UserInvitation>
        </SideContents>
      </section>
      <UserModal
        :isOpenDeleteModal="isOpenDeleteModal"
        :isOpenReSendConfirmModal="isOpenReSendConfirmModal"
        :isOpenReSendModal="isOpenReSendModal"
        :isReSendSuccess="isReSendSuccess"
        :isShowDeleteWarning="isShowDeleteWarning"
        :isDeleteApiLoading="isDeleteApiLoading"
        :isDeleteError="isDeleteError"
        :isUpdateError="isUpdateError"
        :isCreateError="isCreateError"
        :isPasswordResetSuccess="isPasswordResetSuccess"
        :isShowPasswordResetModal="isShowPasswordResetModal"
        :failedInviteUserEmails="failedInviteUserEmails"
        :failedDeleteUserEmails="failedDeleteUserEmails"
        @onClickReSendConfirmModalClose="onClickReSendConfirmModalClose"
        @onClickReSendConfirmButton="onClickReSendButton"
        @onClickReSendModalClose="onClickReSendModalClose"
        @onClickCancelButton="onClickCancelButton"
        @onClickDeleteDone="onClickDeleteDone"
        @onClickDeleteClose="onClickDeleteClose"
        @onClickSaveErrorClose="onClickSaveErrorClose"
        @onClickPasswordResetModalClose="onClickPasswordResetModalClose"
      />
    </Card>
  </section>
</template>

<script lang="ts">
export default {
  name: 'UserManagement',
};
</script>

<script lang="ts" setup>
import Button from '@spectee/components/Atoms/Button/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import SearchForm from '@spectee/components/Atoms/SearchForm/index.vue';
import SideContents from '@spectee/components/Organisms/SideContents/index.vue';
import UserInvitation from '@spectee/components/Organisms/UserInvitation/index.vue';
import PaginationBar from '@spectee/components/Molecules/PaginationBar/index.vue';
import SelectedSettings from '@spectee/components/Molecules/SelectedSettings/index.vue';
import UserList from '@spectee/components/Organisms/UserList/index.vue';
import UserModal from '@spectee/components/Organisms/UserModal/index.vue';
import { Ref, computed, ref, watch, watchEffect } from 'vue';
import { useOverlayLoading } from '@spectee/composables/useOverlayLoading';
import { useGetInvitationBasicDetailsWithoutOrgId } from '@spectee/graphql/queries/getInvitationBasicDetailsWithoutOrgId';
import { useListAllUsers } from '@spectee/graphql/queries/listAllUsers';
import { useInviteMultipleUsers } from '@spectee/graphql/mutations/inviteMultipleUsers';
import { Group, Message, QueryListAllUsersArgs } from '@spectee/graphql/api';
import { useRoute, useRouter } from 'vue-router';
import { useFeedback } from '@spectee/composables/useFeedback';
import { useDeleteAllUsers } from '@spectee/graphql/mutations/deleteAllUsers';
import { userStatus } from '@spectee/ts/userStatus';
import noDataImage from '@spectee/assets/images/img_notFound.png';
import noSearchDataImage from '@spectee/assets/images/img_search_noItem.svg';
import formatDate from '@spectee/ts/formatDate';
import messages from '../../../../messages.json';
import { UserItem, DialogButtonItem } from '@spectee/types/atoms';
import { isNotNullish } from '@spectee/ts/isNotNullish';

const route = useRoute();
const router = useRouter();

interface Props {
  isInviteUser: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const userInvitationRef = ref<InstanceType<typeof UserInvitation> | null>(null);

const failedInviteUserEmails = ref<string[]>([]);
const failedDeleteUserEmails = ref<string[]>([]);

const MAX_DISPLAY_NUM = 50;
const maxDisplayPages = 5;
const currentPage = ref(1);
const isThChecked = ref(false);
const isAllSelected = ref(false);
const checkedItemsCount = ref(0);

const queryParams = route.query;

const isCreateError = ref(false);
const isUpdateError = ref(false);
const isPasswordResetSuccess = ref(false);
const isShowPasswordResetModal = ref(false);

const isEditCancel = ref(false);

const headList = [
  { key: 'name', label: messages.common.headList.name },
  { key: 'displayName', label: messages.common.headList.displayName },
  { key: 'email', label: messages.common.headList.email },
  { key: 'groupName', label: messages.common.headList.groupName },
  { key: 'status', label: messages.common.headList.status },
  { key: 'authority', label: messages.common.headList.authority },
];

//// リストの取得・表示関係の処理

// ユーザー・招待の一覧
const userItems: Ref<UserItem[]> = ref([]);

watch(
  () => route.query,
  () => {
    if (Object.keys(route.query).length === 0) {
      router.push({
        path: route.path,
        query: { ...queryParams }, // Preserve existing query params
      });
    }
  }
);

const {
  getInvitationBasicDetailsRes: invitationBasicDetails,
  refetch: invitationDetailsRefetch,
  loading: isSkeletonForAPI,
} = useGetInvitationBasicDetailsWithoutOrgId();

const {
  listAllUsersRes: listUsersRes,
  loading: isListLoading,
  refetch: listRefetch,
} = useListAllUsers(ref({ filter: {} }) as Ref<QueryListAllUsersArgs>, {});

// ユーザー・招待の一覧を取得、UserItem型に丸めて結合し、userItemsに格納
// userItemsの値は、取得時のほか、フィルタリング実行時・チェックボックス操作時にも変更される為、comuputedで定義せず、定義済みのrefに都度代入する
watchEffect(() => {
  if (listUsersRes.value) {
    userItems.value = (listUsersRes.value as any[]).map(user => ({
      user: {
        email: user.email,
        authority: user.authority,
        auth0UserId: user.auth0UserId ?? '',
        name: user.name ?? '',
        displayName: user.displayName ?? '',
        groupName: user.groups
          ? user.groups.map((group: Group) => group.name).join(',')
          : '',
        groups: user.groups,
        licenses: (user.licenseData ?? []).filter(isNotNullish),
        group: [],
      },
      status: user.status,
      isChecked: false,
      createdAt: formatDate.changeToYYYYMMDD(user.createdAt),
    })) as UserItem[];
  }
});

//// 招待メール再送信関係の処理
const { mutate: inviteMultipleUsers, loading: inviteUsersLoading } =
  useInviteMultipleUsers();

useOverlayLoading('inviteUsersLoading', inviteUsersLoading);

const targetEmailToReSend = ref('');
const isOpenReSendConfirmModal = ref(false);
const isOpenReSendModal = ref(false);
const isReSendSuccess = ref(false);
const { pushFeedback } = useFeedback();

const onClickReSendModalClose = () => {
  isOpenReSendModal.value = false;
};

const onClickReSendConfirmModalClose = () => {
  targetEmailToReSend.value = '';
  isOpenReSendConfirmModal.value = false;
};

const handleInvitationReSend = (targetEmail: string) => {
  targetEmailToReSend.value = targetEmail;
  isOpenReSendConfirmModal.value = true;
};

const onClickReSendButton = async () => {
  isOpenReSendConfirmModal.value = false;
  try {
    const invitation = listUsersRes.value
      .filter(
        user =>
          user.status === userStatus.invited ||
          user.status === userStatus.expired
      )
      .find(user => user.email === targetEmailToReSend.value);
    if (!invitation) throw new Error('failed to specify resend-invitation');

    const params = {
      inviteUsers: [
        {
          authority: invitation.authority.name,
          email: [invitation.email],
          groupIds: invitation.groups.map(group => group.id),
          licenseIds: (invitation.licenseData ?? []).map(
            license => license?.roleId
          ),
        },
      ],
    };
    const resp = await inviteMultipleUsers({ input: params });
    if (resp?.data?.inviteMultipleUsers?.statusCode === Message.success) {
      isReSendSuccess.value = true;
      await listRefetch();
    } else {
      throw new Error('failed to resend invite');
    }
  } catch (e) {
    isReSendSuccess.value = false;
  }
  isOpenReSendModal.value = true;
  targetEmailToReSend.value = '';
};

//// フィルタリング関係の処理
const searchWord = ref('');

// フィルター後のユーザー一覧
const filteredUser = computed(() => {
  if (searchWord.value) {
    const regex = new RegExp(searchWord.value, 'i');
    return userItems.value.filter(
      userItem =>
        regex.test(userItem.user.name) ||
        regex.test(userItem.user.email) ||
        regex.test(userItem.user.authority?.displayName)
    );
  }
  return userItems.value;
});

// フィルタリングが動いた際、フィルター外のユーザーのチェックを外す
watchEffect(() => {
  userItems.value.forEach(user => {
    if (!filteredUser.value.includes(user)) {
      user.isChecked = false;
    }
  });
});

//// チェックボックス関係の処理

// チェックボックスの選択
const onCheckItem = (event: Event, checkedUser: UserItem) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  // チェックされたユーザーの判定は、本来メールアドレスの一致のみで判定できるはず
  // 現状は、アクティブユーザーと同じメールアドレスの招待が作れてしまっているため、アクティブユーザーならauth0UserId,招待ならメールアドレスで判定している
  userItems.value.forEach(user => {
    if (
      (checkedUser.status === user.status &&
        checkedUser.status === userStatus.active &&
        user.user.auth0UserId === checkedUser.user.auth0UserId) ||
      (checkedUser.status === user.status &&
        checkedUser.status !== userStatus.active &&
        user.user.email === checkedUser.user.email)
    ) {
      user.isChecked = target.checked;
    }
  });

  checkedItemsCount.value = checkedFilterItemsLength();
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  if (checkedDisplayUsersCount === displayUsers.value.length) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
};

const checkedFilterItemsLength = () => {
  return filteredUser.value.filter(user => user.isChecked).length;
};

const checkedDisplayItemsLength = () => {
  return displayUsers.value.filter(user => user.isChecked).length;
};

const onCheckHeader = (event: Event) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  userItems.value.forEach(user => {
    if (displayUsers.value.includes(user)) {
      user.isChecked = target.checked;
    }
  });
  checkedItemsCount.value = checkedFilterItemsLength();
};

const toggleAllChecked = (isAllChecked: boolean) => {
  userItems.value.forEach(user => {
    if (filteredUser.value.includes(user)) {
      user.isChecked = isAllChecked;
    }
  });
  isThChecked.value = isAllChecked;
  isAllSelected.value = isAllChecked;
};

const { mutate: deleteAllUsers, loading: isDeleteApiLoading } =
  useDeleteAllUsers();
useOverlayLoading('isDeleteApiLoading', isDeleteApiLoading);

// 削除モーダルの表示制御用変数
const isOpenDeleteModal = ref(false);
const isShowDeleteWarning = ref(false);
const isDeleteError = ref(false);

// ユーザー削除ボタンでモーダル表示
const onClickDeleteModalOpen = () => {
  // 選択されている場合は確認モーダルを表示
  isOpenDeleteModal.value = true;
  isShowDeleteWarning.value = true;
};

// 削除ボタン押下時の処理
const onClickDeleteDone = async () => {
  // 削除対象のユーザーを絞り込み
  const deleteUserIds = userItems.value
    .filter(user => user.isChecked && user.status === userStatus.active)
    .map(user => user.user.auth0UserId);

  // 削除対象の招待を絞り込み
  const deleteInvitationEmails = userItems.value
    .filter(
      user =>
        user.isChecked &&
        (user.status === userStatus.invited ||
          user.status === userStatus.expired)
    )
    .map(user => user.user.email);

  const deleteAllUsersInput = {
    activeUsers: { auth0UserIds: deleteUserIds },
    invitedUsers: { emails: deleteInvitationEmails },
  };

  try {
    const res = await deleteAllUsers({ input: deleteAllUsersInput });

    const deleteRes = res?.data?.deleteAllUsers;
    const failedIds: string[] = deleteRes?.failedIds ?? [];

    if (deleteRes?.statusCode === Message.success) {
      if (failedIds.length === 0) {
        // all deleted successfully
        isOpenDeleteModal.value = false;
        failedDeleteUserEmails.value = [];
        pushFeedback({
          type: 'success',
          heading: messages.user.success.userDelete,
        });
      } else {
        // partial or complete failure — show existing message + failed IDs
        failedDeleteUserEmails.value = failedIds;
        isShowDeleteWarning.value = false;
        isDeleteError.value = true;
        isOpenDeleteModal.value = true; // ensure modal visible
      }
    } else {
      // backend signaled error (statusCode != success)
      failedDeleteUserEmails.value = failedIds;
      isShowDeleteWarning.value = false;
      isDeleteError.value = true;
      isOpenDeleteModal.value = true;
      console.error('deleteAllUsers returned error:', deleteRes?.error);
    }
  } catch (e) {
    console.error(e);

    failedDeleteUserEmails.value = [];
    isShowDeleteWarning.value = false;
    isDeleteError.value = true;
    isOpenDeleteModal.value = true;
  }

  await listRefetch();
};

const onClickCancelButton = () => {
  isOpenDeleteModal.value = false;
  isDeleteError.value = false;
};
const onClickDeleteClose = () => {
  isOpenDeleteModal.value = false;
  isDeleteError.value = false;
};

const isSideContentsOpen = ref(false);
const userInvitationMode = ref('ADD');
const userData = ref<UserItem>({} as UserItem);

const openSideContents = async (mode: string, index?: number) => {
  userInvitationMode.value = mode;
  isSideContentsOpen.value = true;
  if (mode !== 'ADD' && index !== undefined) {
    userData.value = displayUsers.value[index];
  }
  await invitationDetailsRefetch();
};

const sideContentHeading = computed(() => {
  return userInvitationMode.value === 'ADD'
    ? messages.user.userInvitation.title
    : userData.value?.user.name;
});
const closeSideContents = () => {
  isSideContentsOpen.value = false;
};

watch(
  () => props.isInviteUser,
  newVal => {
    if (newVal) openSideContents('ADD');
  },
  { immediate: true }
);

const clickSend = () => {
  userInvitationRef.value?.onClickSend();
};

const clickUpdate = () => {
  userInvitationRef.value?.onClickUpdate();
};

const changeMode = async (mode: string, isEditCancelClicked?: boolean) => {
  userInvitationMode.value = mode;
  isEditCancel.value = !!isEditCancelClicked; //When click close button in edit mode
};

const showUpdateFeedback = async (updateSuccess: boolean) => {
  if (updateSuccess) {
    pushFeedback({
      type: 'success',
      heading: messages.user.success.userUpdate,
    });
    closeSideContents();
    await listRefetch();
  } else {
    isUpdateError.value = true;
  }
};

type InviteResult = {
  isInviteSuccess: boolean;
  failedIds: string[];
};

const showFeedback = async ({ isInviteSuccess, failedIds }: InviteResult) => {
  if (isInviteSuccess) {
    failedInviteUserEmails.value = [];
    isCreateError.value = false;

    pushFeedback({
      type: 'success',
      heading: messages.user.success.userInvite,
    });
    closeSideContents();
    await listRefetch();
  } else {
    failedInviteUserEmails.value = failedIds ?? [];
    isCreateError.value = true;
    if (failedIds.length > 0) {
      closeSideContents();
      await listRefetch();
    }
  }
};

const showPasswordResetModal = (passwordResetRes: boolean) => {
  isPasswordResetSuccess.value = passwordResetRes;
  isShowPasswordResetModal.value = true;
};

const onClickPasswordResetModalClose = () => {
  isShowPasswordResetModal.value = false;
};

const onClickSaveErrorClose = () => {
  if (isCreateError.value) {
    isCreateError.value = false;
  } else {
    isUpdateError.value = false;
  }
};
const getButtonList = computed<DialogButtonItem[]>(() => {
  const buttonList: DialogButtonItem[] = [];

  if (userInvitationMode.value === 'ADD') {
    buttonList[0] = {
      tag: 'button',
      dynamics: 'secondary',
      label: messages.common.cancel,
      click: closeSideContents,
    };
    buttonList[1] = {
      tag: 'button',
      dynamics: 'primary',
      label: messages.user.sendInvitationEmail,
      click: clickSend,
    };
  }

  if (userInvitationMode.value === 'VIEW') {
    buttonList[0] = {
      tag: 'button',
      dynamics: 'secondary',
      label: messages.common.close,
      click: closeSideContents,
    };
    buttonList[1] = {
      tag: 'button',
      dynamics: 'primary',
      label: messages.common.edit,
      click: () => changeMode('EDIT'),
    };
  }

  if (userInvitationMode.value === 'EDIT') {
    buttonList[0] = {
      tag: 'button',
      dynamics: 'secondary',
      label: messages.common.cancel,
      click: () => changeMode('VIEW', true),
    };
    buttonList[1] = {
      tag: 'button',
      dynamics: 'primary',
      label: messages.common.update,
      click: clickUpdate,
    };
  }

  return buttonList;
});

const movePage = (pageNumber: number) => {
  currentPage.value = pageNumber;
};
const totalCount = computed(() => {
  checkedItemsCount.value = checkedFilterItemsLength();
  return filteredUser.value.length;
});

watch(
  () => totalCount.value,
  () => {
    const totalPages = Math.ceil(totalCount.value / MAX_DISPLAY_NUM);

    // Adjust current page if needed (After deleting users)
    if (currentPage.value > totalPages) {
      currentPage.value = Math.max(totalPages, 1); // fallback to page 1 if all pages are gone
    }
  }
);

const start = computed(() => (currentPage.value - 1) * MAX_DISPLAY_NUM);
const end = computed(() =>
  Math.min(start.value + MAX_DISPLAY_NUM, totalCount.value)
);

const displayUsers = computed(() =>
  filteredUser.value.slice(start.value, end.value)
);

const activeUsers = computed<string[]>(() =>
  ((listUsersRes.value ?? []) as Array<{ email?: string; status?: string }>)
    .filter(user => user.status === userStatus.active)
    .map(user => (user.email ?? '').toLowerCase())
);

watchEffect(() => {
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  if (checkedDisplayUsersCount === displayUsers.value.length) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
});

const noDataText = computed(() => {
  if (filteredUser.value.length === 0 && userItems.value.length !== 0) {
    return messages.user.info.noMatchingUsers;
  } else {
    return messages.user.info.noUser;
  }
});

const noDataImageUrl = computed(() => {
  if (filteredUser.value.length === 0 && userItems.value.length !== 0) {
    return noSearchDataImage;
  } else {
    return noDataImage;
  }
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.UserManagement {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__inner {
    margin: $service-unit * 6 0;
  }

  &__card {
    height: 100%;
    display: flex;
    padding: $service-unit * 4 $service-unit * 8 0;
    background-color: $color-gray-20;
  }

  & &__mainSection {
    padding: $service-unit * 4 $service-unit * 4 $service-unit * 6;
    display: flex;
    flex-direction: column;
    overflow: auto;
    background-color: $color-white-100;
    width: 100%;
  }

  &__inner,
  &__list {
    display: flex;
    align-items: center;
  }
  &__selectedSettings {
    margin-bottom: $service-unit * 7;
  }

  & &__searchForm {
    width: 100% * calc(400 / 1104);
    max-width: $service-unit * 100;
  }

  &__list {
    margin-left: auto;
  }

  &__listItem:not(:first-child) {
    margin-left: $service-unit * 6;
  }

  & &__pagination {
    margin-top: $service-unit * 5;
  }

  @media (max-width: 767px) {
    padding: $service-unit * 4 $service-unit * 5 0;
    &__mainSection {
      position: relative;
      height: 100%;
    }
    &__inner {
      flex: 1;
      display: flex;
      flex-flow: row;
      margin-bottom: $service-unit * 3;
    }
    &__card {
      height: 100%;
      display: flex;
      padding: 0;
      background-color: $color-gray-20;
    }
    & &__button {
      font-size: $service-font-size-ppp;
      min-height: $service-unit * 8;
      margin-bottom: $service-unit * 2;
    }

    & &__searchForm {
      width: 50%;
    }
  }

  @media (max-width: 376px) {
    &__list {
      margin-left: $service-unit * 16;
    }
  }

  @media (max-width: 321px) {
    &__list {
      margin-left: $service-unit * 3;
    }
  }
}
</style>
